<template>
  <div>
    <div class="card shadow-sm mb-4">
      <div class="card-header bg-light d-flex justify-content-between align-items-center">
        <h4 class="card-title m-0">
          <i class="fas fa-user-circle me-2 text-primary"></i>My Profile
        </h4>
        <button class="btn btn-outline-primary btn-sm" @click="showModal = true">
          <i class="fas fa-edit me-1"></i> Edit Profile
        </button>
      </div>
      <div class="card-body">
        <div v-if="loading" class="text-center py-5">
          <Spinner spinner-class="mb-1" />
        </div>
        <div v-else>
          <div class="text-center mb-4">
            <h2 class="mb-0">{{ user.first_name }} {{ user.last_name }}</h2>
            <span class="badge bg-secondary">{{ user.role }}</span>
          </div>
          <hr class="border-1 border-info border-opacity-75" />
          <div class="row g-4">
            <div class="col-md-4 text-center">
              <div class="card shadow-sm">
                <div class="card-body">
                  <img
                    class="img-thumbnail bg-light mb-3"
                    width="150"
                    :src="avatarUrl + user.avatar"
                    alt="User Avatar"
                  >
                  <div>
                    <button
                      class="btn btn-primary w-100"
                      @click="$refs.refInputEl.click()"
                    >
                      <i class="fas fa-upload me-1"></i> Upload Photo
                    </button>
                    <input
                      ref="refInputEl"
                      type="file"
                      name="logo"
                      accept=".jpeg,.png,.jpg"
                      hidden
                      @input="changeAvatar"
                    >
                  </div>
                </div>
              </div>
            </div>
            <div class="col-md-8">
              <div class="card shadow-sm h-100">
                <div class="card-header bg-light">
                  <h5 class="m-0">
                    <i class="fas fa-info-circle me-2 text-primary"></i>User Information
                  </h5>
                </div>
                <div class="card-body">
                  <div class="row g-3">
                    <div class="col-md-6">
                      <div class="d-flex">
                        <div class="text-primary me-2">
                          <i class="fas fa-phone"></i>
                        </div>
                        <div>
                          <h6 class="mb-1 text-muted">Phone</h6>
                          <p class="mb-0">{{ user.phone }}</p>
                        </div>
                      </div>
                    </div>
                    <div class="col-md-6">
                      <div class="d-flex">
                        <div class="text-primary me-2">
                          <i class="fas fa-envelope"></i>
                        </div>
                        <div>
                          <h6 class="mb-1 text-muted">Email Address</h6>
                          <p class="mb-0">{{ user.email }}</p>
                        </div>
                      </div>
                    </div>
                    <div class="col-md-6">
                      <div class="d-flex">
                        <div class="text-primary me-2">
                          <i class="fas fa-user-tag"></i>
                        </div>
                        <div>
                          <h6 class="mb-1 text-muted">Role</h6>
                          <p class="mb-0">{{ user.role }}</p>
                        </div>
                      </div>
                    </div>
                    <div class="col-md-6">
                      <div class="d-flex">
                        <div class="text-primary me-2">
                          <i class="fas fa-calendar-alt"></i>
                        </div>
                        <div>
                          <h6 class="mb-1 text-muted">Created</h6>
                          <p class="mb-0">{{ formatDate(user.created_at) }}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="card-footer bg-light">
        <button class="btn btn-outline-secondary" @click="$router.go(-1)">
          <i class="fas fa-arrow-left me-1"></i> Back
        </button>
      </div>
    </div>

    <div class="card shadow-sm mb-4">
      <div class="card-header bg-light">
        <h4 class="card-title m-0">
          <i class="fas fa-lock me-2 text-primary"></i>Change Password
        </h4>
      </div>
      <div class="card-body">
        <form @submit.prevent="updatePassword">
          <div class="row g-3">
            <div class="col-md-6">
              <label class="form-label fw-bold">New Password</label>
              <div class="input-group">
                <span class="input-group-text bg-white">
                  <i class="fas fa-key text-primary"></i>
                </span>
                <input
                  type="password"
                  class="form-control border-start-0"
                  placeholder="Enter new password"
                  v-model="userPass.password"
                >
              </div>
            </div>
            <div class="col-md-6">
              <label class="form-label fw-bold">Confirm Password</label>
              <div class="input-group">
                <span class="input-group-text bg-white">
                  <i class="fas fa-check-circle text-primary"></i>
                </span>
                <input
                  type="password"
                  class="form-control border-start-0"
                  placeholder="Confirm your password"
                  v-model="userPass.password_confirmation"
                >
              </div>
            </div>
          </div>
          <hr class="border-1 border-info border-opacity-75 mt-4" />
          <div class="d-flex justify-content-center mt-3">
            <button type="submit" class="btn btn-primary">
              <i class="fas fa-save me-1"></i> Update Password
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Edit Profile Modal -->
    <Modal :open="showModal" @closed="showModal = false" dialog-size="modal-lg">
      <div class="modal-header bg-light">
        <h5 class="modal-title m-0">
          <i class="fas fa-edit me-2 text-primary"></i>Edit Profile
        </h5>
        <button @click="showModal = false" type="button" class="btn-close outline-none" data-dismiss="modal" aria-label="Close">
        </button>
      </div>
      <div class="modal-body">
        <form @submit.prevent="submitForm">
          <div class="row g-3">
            <div class="col-md-6">
              <label for="first_name" class="form-label fw-bold">First Name</label>
              <div class="input-group">
                <span class="input-group-text bg-white">
                  <i class="fas fa-user text-primary"></i>
                </span>
                <input
                  type="text"
                  class="form-control border-start-0"
                  placeholder="Enter first name"
                  v-model="user.first_name"
                >
              </div>
            </div>
            <div class="col-md-6">
              <label for="last_name" class="form-label fw-bold">Last Name</label>
              <div class="input-group">
                <span class="input-group-text bg-white">
                  <i class="fas fa-user text-primary"></i>
                </span>
                <input
                  type="text"
                  class="form-control border-start-0"
                  placeholder="Enter last name"
                  v-model="user.last_name"
                >
              </div>
            </div>
            <div class="col-md-6">
              <label for="phone" class="form-label fw-bold">Phone</label>
              <div class="input-group">
                <span class="input-group-text bg-white">
                  <i class="fas fa-phone text-primary"></i>
                </span>
                <input
                  type="text"
                  class="form-control border-start-0"
                  placeholder="Enter phone number"
                  v-model="user.phone"
                >
              </div>
            </div>
            <div class="col-md-6">
              <label for="email" class="form-label fw-bold">Email</label>
              <div class="input-group">
                <span class="input-group-text bg-white">
                  <i class="fas fa-envelope text-primary"></i>
                </span>
                <input
                  type="email"
                  class="form-control border-start-0"
                  placeholder="Enter email address"
                  v-model="user.email"
                >
              </div>
            </div>
          </div>
        </form>
      </div>
      <div class="modal-footer bg-light">
        <button type="button" class="btn btn-outline-secondary" data-dismiss="modal" @click="showModal = false">
          <i class="fas fa-times me-1"></i> Cancel
        </button>
        <button type="button" class="btn btn-primary" @click="submitForm">
          <i class="fas fa-save me-1"></i> Save Changes
        </button>
      </div>
    </Modal>
    <!-- Edit Profile Modal End -->
  </div>
</template>

<script>
import userService from "@/api/userService";
import formatDate from "../../utils/formatDate";

export default {
  name: "UserProfile",
  data() {
    return {
      loading: true,
      showModal: false,
      user: {},
      userPass: {
        password: '',
        password_confirmation: ''
      },
      avatarUrl: process.env.VUE_APP_API_IMAGE_URL + 'user/',
    }
  },
  methods: {
    formatDate,
    submitForm: async function () {
      let response = await userService.updateProfile({
        first_name: this.user.first_name,
        last_name: this.user.last_name,
        phone: this.user.phone,
        email: this.user.email
      })
      if (response)
        this.showModal = false
    },
    async updatePassword() {
      await userService.changePassword(this.userPass, this.user.id)
    },
    changeAvatar : async function(file) {
      const fileReader = new FileReader()
      const { files } = file.target
      if (files && files.length) {
        fileReader.readAsDataURL(files[0])
        fileReader.onload = async () => {
          if (typeof fileReader.result === 'string')
            await userService.updateAvatar({ file: fileReader.result })
        }
      }
    }
  },
  created: async function () {
    this.user = await userService.getProfile()
    this.loading = false
  }
}
</script>
